import type { JsonValue } from '#types.js';

import JSON5 from 'json5';
import { z } from 'zod';
import { fromZodError } from 'zod-validation-error';

import { assert, getCaller } from '#errors.js';
import { registerSerializer } from '#serializers.js';
import { prettyJson, tryReadJson } from '#utils.js';
import { isZodType, zodTypeName } from '#zod/types.js';

/**
 * Zod schema for validating date strings that can be parsed by tryReadDate
 */

export const zDateString = z
  .string()
  .describe('A valid date string (ISO format or other supported formats, prefer YYYY-MM-DD)')
  .date('Invalid date format. Must be a valid ISO date string or other supported date format.');

export * from '#zod/types.js';

export interface ZodNullish<T extends z.ZodTypeAny = z.ZodTypeAny> extends z.ZodOptional<z.ZodNullable<T>> {}

/**
 * Pretty prints a Zod schema in a human-readable YAML-like format
 * @param schema - The Zod schema to print
 * @param indent - Current indentation level (for recursion)
 * @returns A human-readable string representation of the schema
 */
export function prettyPrintZod(schema: z.ZodTypeAny, indent: number = 0): string {
  return JSON.stringify(zodToJson(schema), null, 2);
}
export function zodParse<Schema extends z.ZodTypeAny>(
  schema: Schema,
  input: z.input<Schema> | unknown,
  { schemaName = getCaller(), message = 'Given: ````%json%\n```' }: { schemaName?: string; message?: string } = {},
): z.output<Schema> {
  const result = schema.safeParse(input);

  if (result.success) return result.data;
  message = message?.replace('%json%', tryReadJson(input) as string);
  // console.error(`!!!!!!!! <ZOD PARSE ERROR FOR ${schema.description}> !!!!!!!!!!!`);
  // console.error(
  //   `[zodParse] ${schemaName} ${schema.description ?? ''} <- ${prettyJson(input)}\n\nSchema: ${JSON5.stringify(zodSchemaToHumanReadable(schema), null, 2)}\n`,
  // );
  // console.error(result.error.format());
  // console.error(`!!!!!!!! </ZOD PARSE ERROR FOR ${schema.description}> !!!!!!!!!!!`);
  const prefix = [schemaName || undefined, schema.description || undefined, message || undefined]
    .filter((s) => s !== undefined)
    .map((s) => s.trim())
    .join(' ');
  const err = fromZodError(result.error as any, { prefix });
  Object.assign(err, { input });
  throw err;
}

export function assertZodType(value: unknown): asserts value is z.ZodTypeAny {
  assert(isZodType(value), `Value must be a Zod type, got: ${JSON5.stringify(value, null, 2)}`);
}

function asZodTypeAny(value: unknown): z.ZodTypeAny {
  return value as z.ZodTypeAny;
}

export function zodSchemaToHumanReadable(schema: z.ZodTypeAny): unknown {
  if (!isZodType(schema)) return schema;
  if (schema instanceof z.ZodObject) {
    const rec = {} as Record<string, unknown>;
    for (const [key, value] of Object.entries(schema.shape)) {
      rec[key] = isZodType(value) ? zodSchemaToHumanReadable(value) : value;
    }
    return rec;
  } else if (schema instanceof z.ZodArray) {
    assertZodType(schema.element);
    return [zodSchemaToHumanReadable(schema.element)];
  } else if (schema instanceof z.ZodString) {
    return 'string';
  } else if (schema instanceof z.ZodNumber) {
    return 'number';
  } else if (schema instanceof z.ZodBoolean) {
    return 'boolean';
  } else if (schema instanceof z.ZodNull) {
    return 'null';
  } else if (schema instanceof z.ZodUndefined) {
    return 'undefined';
  } else if (schema instanceof z.ZodAny) {
    return 'any';
  } else if (schema instanceof z.ZodUnknown) {
    return 'unknown';
  } else if (schema instanceof z.ZodLiteral) {
    return schema.value;
  } else if (schema instanceof z.ZodNever) {
    return 'never';
  } else if (schema instanceof z.ZodDefault) {
    const val = zodSchemaToHumanReadable(asZodTypeAny(schema._def.innerType));
    const defaultValue =
      typeof schema._def.defaultValue === 'function'
        ? (schema._def.defaultValue as () => unknown)()
        : schema._def.defaultValue;
    return [val, `default: ${prettyJson(defaultValue)}`];
  } else if (schema instanceof z.ZodOptional) {
    const inner = asZodTypeAny(schema.unwrap());
    assertZodType(inner);
    return `${zodSchemaToHumanReadable(inner)} | undefined`;
  } else if (schema instanceof z.ZodEnum) {
    return schema.options.join(' | ');
  } else if (schema instanceof z.ZodUnion) {
    return schema.options.map((option) => zodSchemaToHumanReadable(asZodTypeAny(option)));
  } else if (schema instanceof z.ZodNullable) {
    const inner = asZodTypeAny(schema.unwrap());
    assertZodType(inner);
    return `${zodSchemaToHumanReadable(inner)} | null`;
  } else if (schema instanceof z.ZodRecord) {
    const key = zodSchemaToHumanReadable(asZodTypeAny(schema.keyType));
    const value = zodSchemaToHumanReadable(asZodTypeAny(schema.valueType));
    return `{ [key: ${key}]: ${value} }`;
  } else if (schema instanceof z.ZodIntersection) {
    const left = zodSchemaToHumanReadable(asZodTypeAny(schema._def.left));
    const right = zodSchemaToHumanReadable(asZodTypeAny(schema._def.right));
    return `(${left} & ${right})`;
  } else if (schema instanceof z.ZodDiscriminatedUnion) {
    const discriminator = schema.def.discriminator;
    const options = schema.options.map((option) => zodSchemaToHumanReadable(asZodTypeAny(option))).join(' | ');
    return `(${discriminator} of ${options})`;
  } else if (schema instanceof z.ZodTuple) {
    const elements = schema.def.items.map((item) => zodSchemaToHumanReadable(asZodTypeAny(item)));
    return `[${elements.join(', ')}]`;
  } else if (schema instanceof z.ZodMap) {
    const key = zodSchemaToHumanReadable(asZodTypeAny(schema.keyType));
    const value = zodSchemaToHumanReadable(asZodTypeAny(schema.valueType));
    return `Map<${key}, ${value}>`;
  } else if (schema instanceof z.ZodPipe) {
    return zodSchemaToHumanReadable(asZodTypeAny(schema.in));
  } else {
    return `[${zodTypeName(schema)} ${prettyJson(schema)}]`;
  }
}
export function zodDescriptionPrefix<T extends z.ZodTypeAny>(schema: T, description: string): T {
  return schema.describe([description, schema.description].filter(Boolean).join(' ')) as T;
}

export function zodDescriptionSuffix<T extends z.ZodTypeAny>(schema: T, description: string): T {
  return schema.describe([schema.description, description].filter(Boolean).join(' ')) as T;
}

export function denullify<T extends z.ZodOptional<z.ZodTypeAny>>(schema: T) {
  return z.preprocess((value) => (value === null ? undefined : value), schema);
}

export function smartNullish<T extends z.ZodTypeAny>(schema: T) {
  return z.preprocess((value) => (value === null ? undefined : value), schema).optional();
}

export const ZodPrimitiveJsonValue = z.union([z.string(), z.number(), z.boolean(), z.null(), z.undefined()]);

export const ZodJsonValue: z.ZodType<JsonValue> = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
  z.undefined(),
  z.array(z.lazy(() => ZodJsonValue)),
  z.record(
    z.string(),
    z.lazy(() => ZodJsonValue),
  ),
]);

export function zodToJson(schema: z.ZodTypeAny): JsonValue {
  return z.toJSONSchema(schema, {
    target: 'draft-07',
    io: 'input',
    unrepresentable: 'any',
    cycles: 'ref',
    reused: 'inline',
  }) as JsonValue;
}

registerSerializer('zod', (v: z.ZodTypeAny) => zodToJson(v));
